import React, { RefObject } from 'react';
import { RelBox } from '../../../api/ai.api.types';
import { coverCropRegion, visibleViewport } from '../helper/frameCapture';
import {
    AlignmentEffect,
    TrackerSettings,
    ALIGNMENT_EFFECTS,
    TRACKER_ALGORITHMS,
    TRACKER_KEYPOINT_COUNTS,
    FRAME_COLOR_MODES,
    FRAME_LINE_STYLES,
    DETECTION_SOURCES,
} from '../helper/trackerSettings';
import { TrackerDebugInfo } from '../hooks/useBoxTracker';

interface TrackerDebugOverlayProps {
    debug: TrackerDebugInfo | null;
    serverBox: RelBox | null;
    trackedBox: RelBox | null;
    mode: 'tracking' | 'snap';
    videoRef?: RefObject<HTMLVideoElement | null>;
    settings?: TrackerSettings;
    onSettingsChange?: (settings: TrackerSettings) => void;
}

const EFFECT_LABELS: Record<AlignmentEffect, string> = {
    particles: 'PART',
    blur: 'BLUR',
    metaballs: 'BLOB',
    cornerLines: 'LINE',
    scrim: 'DIM',
};

// Developer-only overlay (enabled via ?aiDebug=1 or localStorage.aiReadingDebug='1'). Draws the
// tracker keypoints (green = alive, red = dead), the raw last server box (blue, dashed — what the
// AI returned, before local tracking), and a metrics line. All coordinates are relative to the
// visible viewport (captures are cropped to it), so anything drawn off here means the pipeline
// itself is off, not the display mapping. The metrics line shows the raw camera resolution vs.
// the cover-crop actually sent, to diagnose camera orientation/aspect issues.
const TrackerDebugOverlay = ({ debug, serverBox, trackedBox, mode, videoRef, settings, onSettingsChange }: TrackerDebugOverlayProps) => {
    const video = videoRef?.current;
    const videoWidth = video?.videoWidth ?? 0;
    const videoHeight = video?.videoHeight ?? 0;
    const viewport = typeof window !== 'undefined' ? visibleViewport() : { width: 0, height: 0 };
    const crop = coverCropRegion(videoWidth, videoHeight, viewport);

    const consensus = debug?.consensus;
    const aliveCount = debug?.keypoints.filter((kp) => kp.alive).length ?? 0;
    const infoParts = [
        `alg ${settings?.algorithm ?? '–'}`,
        `mode ${mode}`,
        `fps ${debug?.fps ?? '–'}`,
        `pts ${aliveCount}/${debug?.keypoints.length ?? 0}`,
        consensus ? `dx ${consensus.dx.toFixed(1)} dy ${consensus.dy.toFixed(1)} s ${consensus.scale.toFixed(3)}` : 'no consensus',
        `cam ${videoWidth}x${videoHeight}`,
        `crop ${Math.round(crop.sw)}x${Math.round(crop.sh)}@${Math.round(crop.sx)},${Math.round(crop.sy)}`,
        `view ${viewport.width}x${viewport.height}`,
        `trk ${debug ? `${debug.frameWidth}x${debug.frameHeight}` : '–'}`,
    ];

    return (
        <div data-testid="tracker-debug-overlay" className={'absolute inset-0 overflow-hidden pointer-events-none'} aria-hidden={true}>
            {serverBox && (
                <div
                    data-testid="debug-server-box"
                    className={'absolute border-2 border-dashed border-blue-400'}
                    style={{ left: `${serverBox.x * 100}%`, top: `${serverBox.y * 100}%`, width: `${serverBox.w * 100}%`, height: `${serverBox.h * 100}%` }}
                >
                    <span className={'absolute -top-5 right-0 text-xs text-blue-300 bg-black bg-opacity-60 rounded px-1'}>AI</span>
                </div>
            )}
            {(debug?.keypoints ?? []).map((kp, index) => (
                <div
                    key={index}
                    data-testid={kp.alive ? 'debug-keypoint-alive' : 'debug-keypoint-dead'}
                    className={'absolute w-2 h-2 rounded-full border ' + (kp.alive ? 'bg-green-400 border-green-200' : 'bg-red-500 border-red-300')}
                    style={{ left: `calc(${kp.x * 100}% - 4px)`, top: `calc(${kp.y * 100}% - 4px)` }}
                />
            ))}
            {settings && onSettingsChange && (
                <div className={'absolute left-2 flex gap-1 pointer-events-auto'} style={{ bottom: '5.5rem' }}>
                    {FRAME_COLOR_MODES.map((colorMode) => (
                        <button
                            key={colorMode}
                            type="button"
                            data-testid={`debug-frame-color-${colorMode}`}
                            onClick={() => onSettingsChange({ ...settings, frameColorMode: colorMode })}
                            className={
                                'text-xs font-mono rounded px-2 py-1 ' +
                                (settings.frameColorMode === colorMode ? 'bg-green-500 text-black' : 'bg-black bg-opacity-70 text-white')
                            }
                        >
                            {colorMode === 'neutral' ? 'NEUT' : colorMode.toUpperCase()}
                        </button>
                    ))}
                    <span className={'w-2'} />
                    {FRAME_LINE_STYLES.map((lineStyle) => (
                        <button
                            key={lineStyle}
                            type="button"
                            data-testid={`debug-frame-line-${lineStyle}`}
                            onClick={() => onSettingsChange({ ...settings, frameLineStyle: lineStyle })}
                            className={
                                'text-xs font-mono rounded px-2 py-1 ' +
                                (settings.frameLineStyle === lineStyle ? 'bg-green-500 text-black' : 'bg-black bg-opacity-70 text-white')
                            }
                        >
                            {lineStyle === 'solid' ? 'SOLID' : 'DASH'}
                        </button>
                    ))}
                </div>
            )}
            {settings && onSettingsChange && (
                <div className={'absolute bottom-16 left-2 flex gap-1 pointer-events-auto'}>
                    {ALIGNMENT_EFFECTS.map((effect) => {
                        const active = settings.effects.includes(effect);
                        const toggled: AlignmentEffect[] = active ? settings.effects.filter((e) => e !== effect) : [...settings.effects, effect];
                        return (
                            <button
                                key={effect}
                                type="button"
                                data-testid={`debug-effect-${effect}`}
                                onClick={() => onSettingsChange({ ...settings, effects: toggled })}
                                className={
                                    'text-xs font-mono rounded px-2 py-1 ' + (active ? 'bg-green-500 text-black' : 'bg-black bg-opacity-70 text-white')
                                }
                            >
                                {EFFECT_LABELS[effect]}
                            </button>
                        );
                    })}
                </div>
            )}
            {settings && onSettingsChange && (
                <div className={'absolute bottom-10 left-2 flex gap-1 pointer-events-auto'}>
                    {TRACKER_ALGORITHMS.map((algorithm) => (
                        <button
                            key={algorithm}
                            type="button"
                            data-testid={`debug-alg-${algorithm}`}
                            onClick={() => onSettingsChange({ ...settings, algorithm })}
                            className={
                                'text-xs font-mono rounded px-2 py-1 ' +
                                (settings.algorithm === algorithm ? 'bg-green-500 text-black' : 'bg-black bg-opacity-70 text-white')
                            }
                        >
                            {algorithm.toUpperCase()}
                        </button>
                    ))}
                    <span className={'w-2'} />
                    {TRACKER_KEYPOINT_COUNTS.map((count) => (
                        <button
                            key={count}
                            type="button"
                            data-testid={`debug-pts-${count}`}
                            onClick={() => onSettingsChange({ ...settings, keypointCount: count })}
                            className={
                                'text-xs font-mono rounded px-2 py-1 ' +
                                (settings.keypointCount === count ? 'bg-green-500 text-black' : 'bg-black bg-opacity-70 text-white')
                            }
                        >
                            {count}p
                        </button>
                    ))}
                    <span className={'w-2'} />
                    {DETECTION_SOURCES.map((source) => (
                        <button
                            key={source}
                            type="button"
                            data-testid={`debug-source-${source}`}
                            onClick={() => onSettingsChange({ ...settings, detectionSource: source })}
                            className={
                                'text-xs font-mono rounded px-2 py-1 ' +
                                (settings.detectionSource === source ? 'bg-green-500 text-black' : 'bg-black bg-opacity-70 text-white')
                            }
                        >
                            {source.toUpperCase()}
                        </button>
                    ))}
                </div>
            )}
            <div className={'absolute bottom-2 left-2 text-xs font-mono text-white bg-black bg-opacity-70 rounded px-2 py-1 whitespace-pre-wrap'}>
                {infoParts.join(' | ')}
            </div>
            {trackedBox && !debug && <div className={'absolute bottom-32 left-2 text-xs font-mono text-yellow-300'}>tracker inactive</div>}
        </div>
    );
};

export default TrackerDebugOverlay;
